import { sql } from 'drizzle-orm';
import { boolean, index, integer, jsonb, pgTable, text, uuid, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

export const resumes = pgTable(
  'resumes',
  {
    id: id(),
    userId: text('user_id').notNull(),
    jobId: text('job_id'),
    label: varchar('label', { length: 200 }).notNull(),
    pages: integer('pages'),
    wordCount: integer('word_count'),
    formatVariant: varchar('format_variant', { length: 24 }).default('au_standard').notNull(),
    pdfR2Key: varchar('pdf_r2_key', { length: 256 }),
    htmlR2Key: varchar('html_r2_key', { length: 256 }),
    sections: jsonb('sections'),
    chosenAchievementIds: jsonb('chosen_achievement_ids').$type<string[]>(),
    linterReport: jsonb('linter_report'),
    // Additive (corpus-résumé dedup/recall, 2026-06-27): the source corpus UUIDs this
    // immutable snapshot was composed from, in a QUERYABLE typed form (not jsonb-spelunking
    // the document). A future dedup/recall layer answers "which résumés reference role/card X"
    // via `WHERE 'X' = ANY(source_role_ids)` / array-overlap. NO-INVENTION: only real
    // roles.id / achievements.id UUIDs that exist in the composed document. Default '{}' so
    // every pre-existing row + every non-corpus writer is unaffected (empty, never null-checked).
    sourceRoleIds: uuid('source_role_ids')
      .array()
      .notNull()
      .default(sql`'{}'::uuid[]`),
    sourceAchievementIds: uuid('source_achievement_ids')
      .array()
      .notNull()
      .default(sql`'{}'::uuid[]`),
    // Additive (generation loops, phase A 2026-07-02): the mint-context stamp —
    // { sourceEpoch, origin: 'user'|'offer', basedOnId?, scoreId? } — so a reader can
    // tell whether this immutable version still matches the current corpusEpoch.
    // NULL on pre-loop rows = "pre-loop, staleness unknown", never a fabricated epoch.
    provenance: jsonb('provenance'),
    // Additive (POLISH-2, living-résumé Phase A, 2026-07-05): the corpus door mints TWO
    // renderings of the SAME corpus — 'raw' (today's default: ghostwritten bullets,
    // otherwise-verbatim summary/descriptors) and 'written' (the whole-document craft
    // pass: arc-guided profile + role-descriptor rewrite, same grounding + lint gates).
    // Both are ordinary `resumes` rows with jobId NULL, so a written cut is byte-compatible
    // with every existing resumes reader (exports.routes.ts's loadArtifact, Drive export,
    // print/.docx) with ZERO new branches — it IS a resumes row. Defaults 'raw' so every
    // pre-existing row (this column didn't exist yet) classifies correctly with no backfill:
    // they were all the one-and-only cut this table ever minted.
    cutKind: varchar('cut_kind', { length: 16 }).default('raw').notNull(),
    immutable: boolean('immutable').default(true).notNull(),
    ...timestamps,
  },
  (t) => ({
    userIdx: index('resumes_user_idx').on(t.userId),
    jobIdx: index('resumes_job_idx').on(t.jobId),
    // GIN indexes so the "which résumés reference role/card X" query is an index hit,
    // not a seq scan, as the table grows. Additive; harmless on the existing rows.
    sourceRoleIdsIdx: index('resumes_source_role_ids_idx').using('gin', t.sourceRoleIds),
    sourceAchievementIdsIdx: index('resumes_source_achievement_ids_idx').using(
      'gin',
      t.sourceAchievementIds,
    ),
    // Additive (POLISH-2): the corpus door's "latest row of THIS cut kind for this
    // user" read (WHERE user_id = $1 AND job_id IS NULL AND cut_kind = $2 ORDER BY
    // created_at DESC LIMIT 1) is an index hit, not a seq scan, as the table grows.
    cutKindIdx: index('resumes_cut_kind_idx').on(t.cutKind),
  }),
);

export const coverLetters = pgTable(
  'cover_letters',
  {
    id: id(),
    userId: text('user_id').notNull(),
    jobId: text('job_id'),
    label: varchar('label', { length: 200 }).notNull(),
    formatVariant: varchar('format_variant', { length: 24 }).default('au_standard').notNull(),
    body: text('body').notNull(),
    wordCount: integer('word_count'),
    fkGradeLevel: integer('fk_grade_level'),
    pdfR2Key: varchar('pdf_r2_key', { length: 256 }),
    linterReport: jsonb('linter_report'),
    // GATE-1/GATE-2 (GenerationGate.ts, Phase B): the merged verdict (TailoredProseGate's
    // hard/soft split + the NEW entity-binding pass) — mirrors alignment_essays'
    // own grounding_report column exactly (that one persisted, this one previously did
    // not: the response computed `grounding` fresh every time but never stored it,
    // "add where absent" per the brief). Additive (rule #11); null on pre-existing rows.
    groundingReport: jsonb('grounding_report'),
    tonePreset: varchar('tone_preset', { length: 32 }),
    // Additive (generation loops, phase A 2026-07-02): the mint-context stamp —
    // { sourceEpoch, origin: 'user'|'offer', basedOnId?, scoreId? }. basedOnId carries the
    // refine lineage (the prior cover_letters row). NULL on pre-loop rows, never fabricated.
    provenance: jsonb('provenance'),
    immutable: boolean('immutable').default(true).notNull(),
    ...timestamps,
  },
  (t) => ({
    userIdx: index('cover_letters_user_idx').on(t.userId),
    jobIdx: index('cover_letters_job_idx').on(t.jobId),
  }),
);

export const tailoringRuns = pgTable('tailoring_runs', {
  id: id(),
  userId: text('user_id').notNull(),
  jobId: text('job_id').notNull(),
  resumeId: text('resume_id'),
  coverLetterId: text('cover_letter_id'),
  promptHash: varchar('prompt_hash', { length: 64 }),
  promptCacheHit: boolean('prompt_cache_hit'),
  inputTokens: integer('input_tokens'),
  outputTokens: integer('output_tokens'),
  cachedTokens: integer('cached_tokens'),
  durationMs: integer('duration_ms'),
  modelId: varchar('model_id', { length: 64 }),
  gapTranslations: jsonb('gap_translations'),
  prompts: jsonb('prompts'),
  ...timestamps,
});
